import { createStreamTelemetry } from "./streamTelemetry";
import type { BrowserStreamReport } from "../../../../src/shared/agenticStreamTelemetry";
import { SseLineParser } from "./sseLineParser";
import type { VercelUiStreamPart } from "./types";

/**
 * Transport for the agentic chat SSE stream with network-disconnection
 * recovery (SW-9957).
 *
 * Clients opt in via `x-vercel-ai-ui-message-stream: v1`. The server dual-emits
 * legacy id-less frames for backward compatibility, but this transport consumes
 * only id-tagged Vercel UI message stream parts (`id: N` + `data: {"type":...}`)
 * and the terminal `[DONE]` marker. UI state is driven by those parts through
 * {@link AgenticVercelStreamConsumer}.
 */

interface AgenticStreamReader {
  read(): Promise<{ done: boolean; value?: Uint8Array }>;
}

export interface AgenticStreamResponseLike {
  status: number;
  headers?: { get(name: string): string | null };
  body: { getReader(): AgenticStreamReader } | null;
}

export type AgenticWireFrame = {
  id: string | null;
  data: string;
};

export interface ResumableAgenticStreamHandlers {
  /**
   * Optional passthrough of id-tagged SSE frames (and `[DONE]`) for consumers
   * that parse the wire format themselves (e.g. AI SDK useChat).
   */
  onWireFrame?: (frame: AgenticWireFrame) => void;
  /**
   * Receives each parsed Vercel UI stream part exactly once, in order. Throwing
   * stops the session immediately (no resume) and routes the error to onFailure.
   */
  onVercelPart: (part: VercelUiStreamPart, eventId: string) => void;
  /** The stream supplied a terminal event or a recovered saved response. */
  onDone: () => void;
  /** The stream failed and could not be resumed. */
  onFailure: (error: unknown) => void;
}

export interface RunResumableAgenticStreamOptions {
  clientRequestId?: string;
  onTelemetry?: (event: BrowserStreamReport) => void;
  /** Issues the initial POST. Must throw for non-streamable responses (HTTP errors). */
  createInitialResponse: () => Promise<AgenticStreamResponseLike>;
  /** Issues the resume GET for a dropped stream. */
  createResumeResponse: (
    chatId: string,
    lastEventId: string | null,
    userMessageId?: string,
  ) => Promise<AgenticStreamResponseLike>;
  handlers: ResumableAgenticStreamHandlers;
  /** Chat id known before the stream starts (absent for brand-new chats). */
  initialChatId?: string | null;
  signal?: AbortSignal | null;
  /** Overridable for tests; defaults to an abort/online-aware setTimeout. */
  waitBeforeRetry?: (delayMs: number, signal?: AbortSignal | null) => Promise<void>;
  /** Backoff schedule between resume attempts; the first attempt is immediate. */
  resumeRetryDelaysMs?: number[];
}

const DEFAULT_RESUME_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 30000];

/** Ceiling on reconnects per session; guards against pathological replay loops. */
const MAX_RESUME_CYCLES = 20;
const MISSING_RESPONSE_MESSAGE = "The response was interrupted and could not be recovered. Please try again.";

/** Wraps errors that must stop the session (no resume) on their way to onFailure. */
class FatalStreamError extends Error {
  constructor(readonly originalError: unknown) {
    super(originalError instanceof Error ? originalError.message : String(originalError));
    this.name = "FatalStreamError";
  }
}

function isAbortLikeError(error: unknown, signal?: AbortSignal | null): boolean {
  if (signal?.aborted) return true;
  return error instanceof Error && error.name === "AbortError";
}

function parseVercelPart(data: string): VercelUiStreamPart | null {
  if (data.trim() === "[DONE]") {
    return null;
  }
  try {
    const parsed = JSON.parse(data) as unknown;
    if (parsed && typeof parsed === "object" && typeof (parsed as VercelUiStreamPart).type === "string") {
      return parsed as VercelUiStreamPart;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Waits for the given delay, resolving early when the browser regains
 * connectivity or the stream is aborted.
 */
function defaultWaitBeforeRetry(delayMs: number, signal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      if (typeof window !== "undefined") {
        window.removeEventListener("online", finish);
      }
      signal?.removeEventListener("abort", finish);
      resolve();
    };
    const timeout = setTimeout(finish, delayMs);
    if (typeof window !== "undefined") {
      window.addEventListener("online", finish);
    }
    signal?.addEventListener("abort", finish);
  });
}

export async function runResumableAgenticStream(
  options: RunResumableAgenticStreamOptions,
): Promise<void> {
  const {
    createInitialResponse,
    createResumeResponse,
    handlers,
    signal = null,
    waitBeforeRetry = defaultWaitBeforeRetry,
    resumeRetryDelaysMs = DEFAULT_RESUME_RETRY_DELAYS_MS,
  } = options;

  const telemetry = createStreamTelemetry(options);
  let terminalError = false;
  let recovered = false;
  let chatId: string | null = options.initialChatId ?? null;
  let lastEventId: string | null = null;
  let userMessageId: string | null = null;
  let sawTerminal = false;
  let resumeCycles = 0;
  const legacyToolStartMetadataByCallId = new Map<string, Record<string, unknown>>();
  /** Parts on the current connection that advanced state (not ignored). */
  let progressThisConnection = 0;
  /** Recovery responses contain legacy-only terminal events, unlike normal dual-format streams. */
  let recoveredMessageSeen = false;

  const emitWireFrame = (id: string | null, data: string): void => {
    if (!handlers.onWireFrame) return;
    if (id !== null || data.trim() === "[DONE]") {
      handlers.onWireFrame({ id, data });
    }
  };

  const consumeFrame = (id: string | null, data: string): void => {
    telemetry.frame(id);
    emitWireFrame(id, data);

    if (data.trim() === "[DONE]") {
      sawTerminal = true;
      if (id) {
        lastEventId = id;
      }
      progressThisConnection += 1;
      return;
    }

    if (!id) {
      // Legacy id-less frames are ignored; Vercel parts carry monotonic ids.
      try {
        const legacy = JSON.parse(data) as Record<string, unknown>;
        if (
          legacy?.event === "chat_acknowledged"
          && typeof legacy.chatId === "string"
          && legacy.chatId
        ) {
          chatId = legacy.chatId;
          if (typeof legacy.userMessageId === "string" && legacy.userMessageId) {
            userMessageId = legacy.userMessageId;
          }
        }
        if (legacy?.event === "tool_start" && typeof legacy.callId === "string" && legacy.callId) {
          legacyToolStartMetadataByCallId.set(legacy.callId, legacy);
        }
        // Handle recovered_message from stream resume: the server persisted partial/complete
        // content on disconnect and is replaying it so the client can display it.
        // Clear any previously accumulated text first to avoid duplication, then replace
        // with the full recovered content.
        if (legacy?.event === "recovered_message" && typeof legacy.content === "string") {
          progressThisConnection += 1;
          recoveredMessageSeen = true;
          try {
            // Clear accumulated text to prevent duplication with any partially-received content
            handlers.onVercelPart({ type: "text-end" } as VercelUiStreamPart, `recovered-clear-${legacy.messageId ?? "0"}`);
            // Emit full recovered content as replacement
            handlers.onVercelPart({ type: "text-delta", delta: legacy.content } as VercelUiStreamPart, `recovered-${legacy.messageId ?? "0"}`);
          } catch (error) {
            throw new FatalStreamError(error);
          }
        }
        if (legacy?.event === "complete") {
          if (recoveredMessageSeen) {
            try {
              handlers.onVercelPart(
                { type: "finish" } as VercelUiStreamPart,
                `legacy-complete-${legacy.chatId ?? "0"}`,
              );
            } catch (error) {
              throw new FatalStreamError(error);
            }
            sawTerminal = true;
          }
          progressThisConnection += 1;
        }
      } catch (e) {
        // Re-throw FatalStreamError; ignore non-JSON legacy noise
        if (e instanceof FatalStreamError) throw e;
      }
      return;
    }

    let part = parseVercelPart(data);
    if (!part) {
      return;
    }

    if (part.type === "tool-input-start") {
      const toolCallId = typeof part.toolCallId === "string" ? part.toolCallId : "";
      const legacyMetadata = toolCallId ? legacyToolStartMetadataByCallId.get(toolCallId) : undefined;
      if (legacyMetadata) {
        const enrichedPart: VercelUiStreamPart = { ...part };
        for (const [key, value] of Object.entries(legacyMetadata)) {
          if (value === undefined || key === "event") continue;
          if (key === "callId") {
            if (typeof enrichedPart.toolCallId !== "string") {
              enrichedPart.toolCallId = value;
            }
            continue;
          }
          if (key === "tool") {
            if (typeof enrichedPart.toolName !== "string") {
              enrichedPart.toolName = value;
            }
            continue;
          }
          if (!(key in enrichedPart)) {
            enrichedPart[key] = value;
          }
        }
        part = enrichedPart;
        legacyToolStartMetadataByCallId.delete(toolCallId);
      }
    }

    if (
      part.type === "data-chat_acknowledged"
      && part.data
      && typeof part.data === "object"
      && !Array.isArray(part.data)
      && typeof (part.data as Record<string, unknown>).chatId === "string"
      && (part.data as Record<string, unknown>).chatId
    ) {
      chatId = (part.data as Record<string, unknown>).chatId as string;
      if (typeof (part.data as Record<string, unknown>).userMessageId === "string") {
        userMessageId = (part.data as Record<string, unknown>).userMessageId as string;
      }
    }

    lastEventId = id;
    progressThisConnection += 1;

    if (part.type === "error") terminalError = true;
    if (part.type === "finish" || part.type === "error") {
      sawTerminal = true;
    }

    try {
      handlers.onVercelPart(part, id);
    } catch (error) {
      throw new FatalStreamError(error);
    }
  };

  const readStream = async (response: AgenticStreamResponseLike): Promise<void> => {
    if (!response.body) {
      throw new Error("Response body is not readable");
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    const parser = new SseLineParser();

    for (;;) {
      const { done, value } = await reader.read();
      if (value) telemetry.bytes(value.byteLength);
      const text = value ? decoder.decode(value, { stream: !done }) : done ? decoder.decode() : "";
      const frames = done ? [...parser.push(text), ...parser.end()] : parser.push(text);
      for (const frame of frames) {
        consumeFrame(frame.id, frame.data);
      }
      if (done) {
        return;
      }
    }
  };

  const fetchResumedStream = async (
    disconnectError: unknown,
  ): Promise<AgenticStreamResponseLike> => {
    const resumeChatId = chatId;
    if (!resumeChatId || resumeCycles >= MAX_RESUME_CYCLES) {
      throw new FatalStreamError(disconnectError);
    }
    resumeCycles += 1;
    let resumeFailure = disconnectError;

    for (let attempt = 0; attempt <= resumeRetryDelaysMs.length; attempt += 1) {
      if (attempt > 0) {
        await waitBeforeRetry(resumeRetryDelaysMs[attempt - 1], signal);
      }
      if (signal?.aborted) {
        throw new FatalStreamError(disconnectError);
      }

      let response: AgenticStreamResponseLike;
      try {
        telemetry.reconnect();
        response = userMessageId
          ? await createResumeResponse(resumeChatId, lastEventId, userMessageId)
          : await createResumeResponse(resumeChatId, lastEventId);
        telemetry.attempt(response.status);
      } catch (error) {
        telemetry.attempt(null, error);
        if (isAbortLikeError(error, signal)) {
          throw new FatalStreamError(error);
        }
        continue;
      }

      if (response.status === 204) {
        // No live stream and no saved reply is not evidence of completion. A
        // persistence race can resolve on the next attempt; otherwise surface
        // the interrupted response rather than silently declaring success.
        resumeFailure = new Error(MISSING_RESPONSE_MESSAGE);
        continue;
      }
      if (response.status >= 500 || response.status === 429 || response.status === 408) {
        continue;
      }
      if (response.status >= 400) {
        throw new FatalStreamError(disconnectError);
      }

      return response;
    }

    throw new FatalStreamError(resumeFailure);
  };

  try {
    let response: AgenticStreamResponseLike;
    try {
      response = await createInitialResponse();
      telemetry.response(response, false);
    } catch (error) {
      telemetry.connection("initial_error", error);
      throw new FatalStreamError(error);
    }

    let isResumedConnection = false;
    while (!sawTerminal) {
      let streamError: unknown = null;
      progressThisConnection = 0;
      try {
        await readStream(response);
      } catch (error) {
        if (error instanceof FatalStreamError) {
          throw error;
        }
        streamError = error;
      }

      telemetry.connection(streamError ? "read_error" : sawTerminal ? "terminal" : "eof", streamError);
      if (sawTerminal) {
        break;
      }
      if (streamError && isAbortLikeError(streamError, signal)) {
        throw new FatalStreamError(streamError);
      }
      if (signal?.aborted) {
        throw new FatalStreamError(
          streamError ?? new DOMException("The user aborted a request.", "AbortError"),
        );
      }
      if (isResumedConnection && !streamError && progressThisConnection === 0) {
        throw new FatalStreamError(new Error(MISSING_RESPONSE_MESSAGE));
      }

      const resumed = await fetchResumedStream(
        streamError ?? new Error("Stream ended before completion"),
      );
      response = resumed;
      recovered = true;
      telemetry.response(response, true);
      isResumedConnection = true;
    }

    telemetry.finish(terminalError ? "server_error" : recovered ? "recovered" : "completed");
    handlers.onDone();
  } catch (error) {
    const originalError = error instanceof FatalStreamError ? error.originalError : error;
    telemetry.finish(signal?.aborted ? "aborted" : "failed", originalError);
    handlers.onFailure(originalError);
  }
}
